import React from 'react';
import { Collapse as MuiCollapse, CollapseProps as MuiCollapseProps } from '@mui/material';
import { Box } from '../Box';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export interface CollapseProps
  extends Omit<MuiCollapseProps, keyof PounceStyleProps>, PounceStyleProps {
  /** Whether the children are visible or collapsed */
  isOpen: boolean;

  /**
   * Whether the opacity should be animated along with the height
   * @default true
   */
  animateOpacity?: boolean;

  /**
   * The duration of the animation in ms
   *
   * @default Dynamic depending on the height
   */
  duration?: number;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiCollapseProps['sx'];
}

/**
 * A Collapse component is simply a wrapper that will slowly reveal its children by gradually
 * animating the total height until all of them are visible
 */
export const Collapse = React.forwardRef<HTMLDivElement, CollapseProps>(function Collapse(
  { isOpen, animateOpacity = true, duration, children, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Combine pounce-generated styles with user sx
  const combinedSx: MuiCollapseProps['sx'] = {
    ...pounceStyles,
    ...(sx || {}),
  } as MuiCollapseProps['sx'];

  // Wrap children to add opacity animation when enabled
  const content = animateOpacity ? (
    <Box
      sx={{
        opacity: isOpen ? 1 : 0,
        transition: `opacity ${
          duration === undefined ? 300 : duration
        }ms cubic-bezier(0.4, 0, 0.2, 1)`,
      }}
    >
      {children}
    </Box>
  ) : (
    <Box>{children}</Box>
  );

  return (
    <MuiCollapse
      ref={ref}
      in={isOpen}
      timeout={duration === undefined ? 'auto' : duration}
      sx={combinedSx}
      {...otherProps}
    >
      {content}
    </MuiCollapse>
  );
}) as React.FC<CollapseProps>;
